import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { pickLocale, type ListImageCharacteristic, type Value } from '../../schema';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { useRuleResults } from '../../rules/RuleResultsContext';
import { useEffectiveValuePrice, useThemeStyle, useThemeStyleOn, useValuePriceFormatter } from '../../state/ComponentContext';
import { useTranslation } from '../../i18n';
import { QtyInput } from './QtyInput';
import { InfoIcon } from '../InfoIcon';
import { InfoPopover } from '../InfoPopover';
import { HtmlContent } from '../HtmlContent';
import { getModalAdapter, type ModalAdapter } from '../../api/modalAdapter';
import { useWebshopContext } from '../../state/WebshopContext';

interface ListImageViewProps {
    characteristic: ListImageCharacteristic;
    locale: string | undefined;
}

interface PopoverState {
    valueId: number;
    html: string;
    /** Value label — surfaced as the modal-mode header title when the
     *  popover auto-promotes to modal due to long content. Ignored in
     *  tooltip mode. */
    title: string;
}

/**
 * Tile grid of Values for the listimage Characteristic.
 *
 * Two admin-controlled rendering knobs:
 * - `listImageStyle` (Ansicht / `listimage_style` on the wire): `'0'`
 *   shows tiles without a per-tile caption, `'1'` shows the title under
 *   the thumbnail. Default `'0'` to match legacy.
 * - `thumbStyle` (Darstellung / `thumbstyle` on the wire): `'inline'`
 *   keeps the grid always visible (default), `'collapse'` hides it
 *   behind an expandable header that summarises the current pick, and
 *   `'overlay'` opens the grid in a centred modal triggered by that
 *   header.
 *
 * In `collapse` and `overlay` modes a single-pick selection auto-closes
 * the wrapper; multi-pick stays open so the user can keep choosing.
 */
export function ListImageView({ characteristic, locale }: ListImageViewProps) {
    const selection = useCharacteristicSelection(characteristic.id);
    const { setPickQty } = useStoreActions();
    const qtyMode = characteristic.quantityMode;
    if (!qtyMode) return <ListImagePicker characteristic={characteristic} locale={locale} />;

    if (!characteristic.isMultiple) {
        return (
            <div className="cfg-qty-block">
                <QtyInput
                    mode={qtyMode}
                    min={characteristic.minQty}
                    max={characteristic.maxQty}
                    value={selection?.kind === 'single' ? selection.qty : undefined}
                    onChange={(qty) => setPickQty(characteristic.id, qty)}
                    label={pickLocale(characteristic.title, locale) ?? ''}
                />
                <ListImagePicker characteristic={characteristic} locale={locale} />
            </div>
        );
    }

    // Multi-pick: one quantity per picked image, listed under the grid.
    const picked = selection?.kind === 'multi' ? selection.valueIds : [];
    return (
        <div className="cfg-qty-block">
            <ListImagePicker characteristic={characteristic} locale={locale} />
            {picked.length > 0 && (
                <ul className="cfg-qty-list">
                    {picked.map((id) => {
                        const value = characteristic.values.find((v) => v.id === id);
                        if (!value) return null;
                        const label = pickLocale(value.title, locale) ?? value.value ?? `#${value.id}`;
                        return (
                            <li key={id} className="cfg-qty-list__item">
                                <QtyInput
                                    mode={qtyMode}
                                    min={characteristic.minQty}
                                    max={characteristic.maxQty}
                                    value={selection?.kind === 'multi' ? selection.qtys?.[id] : undefined}
                                    onChange={(qty) => setPickQty(characteristic.id, qty, id)}
                                    label={label}
                                />
                                <span>{label}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function ListImagePicker({ characteristic, locale }: ListImageViewProps) {
    const { t } = useTranslation();
    const selection = useCharacteristicSelection(characteristic.id);
    const { selectValue, toggleValue } = useStoreActions();
    const { blacklistedValues } = useRuleResults();
    const formatValuePrice = useValuePriceFormatter();
    const effectivePrice = useEffectiveValuePrice();
    const isMulti = characteristic.isMultiple;
    const showCaption = characteristic.listImageStyle === '1';
    const thumbStyle = characteristic.thumbStyle ?? 'inline';

    const selectedIds: readonly number[] =
        isMulti
            ? (selection?.kind === 'multi' ? selection.valueIds : [])
            : (selection?.kind === 'single' && selection.valueId !== null ? [selection.valueId] : []);

    const visibleValues = characteristic.values.filter(
        (v) => !blacklistedValues.has(v.id),
    );

    if (visibleValues.length === 0) {
        return <p className="cfg-group__empty">{t('characteristic.no_images')}</p>;
    }

    // Header summary: legacy `option/type/thumb.js:getCollapseHeader`
    // shows the selected Value's THUMBNAIL plus its label, not just text.
    // Mirrors that here — single-pick uses the picked Value's thumb,
    // multi-pick stays text-only ("3 ausgewählt"-style not implemented
    // yet, list of labels is good enough for now).
    const selectedValues = visibleValues.filter((v) => selectedIds.includes(v.id));
    const headerPick = !isMulti && selectedValues.length === 1 ? selectedValues[0] : undefined;
    const headerThumb = headerPick?.thumbnail;
    const headerLabel: string = selectedValues.length === 0
        ? t('listimage.no_selection')
        : selectedValues
              .map((v) => pickLocale(v.title, locale) ?? v.value ?? `#${v.id}`)
              .join(', ');
    const overlayTitle = pickLocale(characteristic.title, locale)
        ?? t('fallback.group_title', { id: characteristic.id });

    const handlePick = (valueId: number, close: () => void) => {
        if (isMulti) {
            toggleValue(characteristic.id, valueId);
            // Stay open — user usually wants to pick more in multi-mode.
        } else {
            selectValue(characteristic.id, valueId);
            close();
        }
    };

    // Relative-mode base for single-pick (multi-pick has no base): the
    // currently picked Value's effective price.
    const currentPickValue = !isMulti
        ? visibleValues.find((v) => selectedIds.includes(v.id))
        : undefined;
    const currentPickPrice = currentPickValue
        ? effectivePrice(currentPickValue, characteristic)
        : undefined;
    const priceFor = (value: Value) =>
        formatValuePrice(effectivePrice(value, characteristic), currentPickPrice);

    if (thumbStyle === 'collapse') {
        return (
            <CollapseShell thumb={headerThumb} label={headerLabel}>
                {(close) => (
                    <TileGrid
                        values={visibleValues}
                        selectedIds={selectedIds}
                        showCaption={showCaption}
                        isMulti={isMulti}
                        locale={locale}
                        onPick={(id) => handlePick(id, close)}
                        priceFor={priceFor}
                    />
                )}
            </CollapseShell>
        );
    }

    if (thumbStyle === 'overlay') {
        return (
            <OverlayShell thumb={headerThumb} label={headerLabel} title={overlayTitle}>
                {(close) => (
                    <TileGrid
                        values={visibleValues}
                        selectedIds={selectedIds}
                        showCaption={showCaption}
                        isMulti={isMulti}
                        locale={locale}
                        onPick={(id) => handlePick(id, close)}
                        priceFor={priceFor}
                    />
                )}
            </OverlayShell>
        );
    }

    return (
        <TileGrid
            values={visibleValues}
            selectedIds={selectedIds}
            showCaption={showCaption}
            isMulti={isMulti}
            locale={locale}
            priceFor={priceFor}
            onPick={(id) => handlePick(id, () => {})}
        />
    );
}

interface TileGridProps {
    values: readonly Value[];
    selectedIds: readonly number[];
    showCaption: boolean;
    isMulti: boolean;
    locale: string | undefined;
    onPick: (valueId: number) => void;
    /** Optional per-Value price suffix (already formatted). Empty string → no suffix shown. */
    priceFor: (value: Value) => string;
}

function TileGrid({ values, selectedIds, showCaption, isMulti, locale, onPick, priceFor }: TileGridProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState<PopoverState | null>(null);
    const triggerRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

    return (
        <div
            className="cfg-listimage"
            role={isMulti ? 'group' : 'radiogroup'}
            data-configento-show-caption={showCaption || undefined}
        >
            {values.map((value) => {
                const label    = pickLocale(value.title, locale) ?? value.value ?? `#${value.id}`;
                const info     = pickLocale(value.info, locale);
                const additionalInfo = pickLocale(value.additionalInfo, locale);
                const selected = selectedIds.includes(value.id);
                const infoOpen = open?.valueId === value.id;
                const priceSuffix = priceFor(value);
                return (
                    <div key={value.id} className="cfg-listimage__cell">
                        <button
                            type="button"
                            className="cfg-listimage__tile"
                            role={isMulti ? 'listitem' : 'radio'}
                            aria-pressed={isMulti ? selected : undefined}
                            aria-checked={isMulti ? undefined : selected}
                            data-configento-value-id={value.id}
                            data-configento-selected={selected || undefined}
                            onClick={() => onPick(value.id)}
                        >
                            {value.thumbnail ? (
                                <img
                                    className="cfg-listimage__thumb"
                                    src={value.thumbnail}
                                    alt={value.thumbnailAlt ?? label}
                                    loading="lazy"
                                />
                            ) : (
                                <div className="cfg-listimage__thumb" aria-hidden="true" />
                            )}
                            {showCaption && (
                                <div className="cfg-listimage__caption">
                                    {label}
                                    {priceSuffix && (
                                        <span className="cfg-listimage__price"> {priceSuffix}</span>
                                    )}
                                </div>
                            )}
                            {/* Legacy appends additional_info to the tile,
                                visible below the image (image-picker
                                `data-cf-additionalInfo`) — not in a popover. */}
                            {additionalInfo && (
                                <HtmlContent
                                    tag="span"
                                    className="cfg-listimage__additional-info"
                                    html={additionalInfo}
                                />
                            )}
                        </button>
                        {info && (
                            <button
                                type="button"
                                ref={(el) => {
                                    if (el) triggerRefs.current.set(value.id, el);
                                    else triggerRefs.current.delete(value.id);
                                }}
                                className="cfg-info-trigger cfg-listimage__info-trigger"
                                aria-expanded={infoOpen}
                                aria-label={t('characteristic.show_info')}
                                title={t('characteristic.show_info')}
                                onClick={() =>
                                    setOpen(infoOpen ? null : { valueId: value.id, html: info, title: label })
                                }
                            >
                                <InfoIcon />
                            </button>
                        )}
                    </div>
                );
            })}
            {open && (
                <InfoPopover
                    html={open.html}
                    title={open.title}
                    triggerEl={triggerRefs.current.get(open.valueId) ?? null}
                    onClose={() => setOpen(null)}
                />
            )}
        </div>
    );
}

interface ShellProps {
    /** Selected Value's thumbnail URL (single-pick). Undefined → label-only header. */
    thumb: string | undefined;
    /** Selected Value label, or "no selection" fallback. */
    label: string;
    children: (close: () => void) => ReactNode;
}

interface OverlayShellProps extends ShellProps {
    /** Characteristic title shown in the modal header bar. */
    title: string;
}

function ShellHeaderPreview({ thumb, label }: { thumb: string | undefined; label: string }) {
    return (
        <>
            {thumb && (
                <img
                    className="cfg-listimage-shell__thumb"
                    src={thumb}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                />
            )}
            <span className="cfg-listimage-shell__summary">{label}</span>
        </>
    );
}

function CollapseShell({ thumb, label, children }: ShellProps) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const close = () => setExpanded(false);
    return (
        <div className="cfg-listimage-shell cfg-listimage-shell--collapse" data-configento-expanded={expanded || undefined}>
            <button
                type="button"
                className="cfg-listimage-shell__header"
                aria-expanded={expanded}
                onClick={() => setExpanded((e) => !e)}
            >
                <ShellHeaderPreview thumb={thumb} label={label} />
                <span className="cfg-listimage-shell__chevron" aria-hidden="true">
                    {expanded ? '▴' : '▾'}
                </span>
                <span className="cfg-visually-hidden">
                    {expanded ? t('listimage.collapse') : t('listimage.expand')}
                </span>
            </button>
            {expanded && (
                <div className="cfg-listimage-shell__body">{children(close)}</div>
            )}
        </div>
    );
}

function OverlayShell({ thumb, label, title, children }: OverlayShellProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);
    // Snapshot the adapter at open-time so a late registration doesn't
    // confuse an already-open modal mid-flight; capture-on-open is fine
    // because adapters are installed before bundle boot.
    const adapter = open ? getModalAdapter() : undefined;

    return (
        <div className="cfg-listimage-shell cfg-listimage-shell--overlay">
            <button
                type="button"
                className="cfg-listimage-shell__header"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen(true)}
            >
                <ShellHeaderPreview thumb={thumb} label={label} />
                <span className="cfg-listimage-shell__chevron" aria-hidden="true">⤢</span>
                <span className="cfg-visually-hidden">{t('listimage.open_overlay')}</span>
            </button>
            {open && (adapter
                ? (
                    <AdapterOverlay adapter={adapter} title={title} onClose={close}>
                        {children(close)}
                    </AdapterOverlay>
                )
                : (
                    <DefaultPortalOverlay title={title} onClose={close}>
                        {children(close)}
                    </DefaultPortalOverlay>
                )
            )}
        </div>
    );
}

/**
 * Default modal: bundle-controlled portal + chrome. Used in admin-iframe
 * preview where no host-shop modal system is available.
 */
function DefaultPortalOverlay({
    title, onClose, children,
}: { title: string; onClose: () => void; children: ReactNode }) {
    const { t } = useTranslation();
    const themeVars = useThemeStyle();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (typeof document === 'undefined') return null;
    return createPortal(
        <div
            className="cfg-listimage-overlay"
            style={themeVars}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="cfg-listimage-overlay__panel">
                <div className="cfg-listimage-overlay__header">
                    <h2 className="cfg-listimage-overlay__title">{title}</h2>
                    <button
                        type="button"
                        className="cfg-listimage-overlay__close"
                        aria-label={t('characteristic.close')}
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>
                {children}
            </div>
        </div>,
        document.body,
    );
}

/**
 * Adapter-driven modal: bundle hands the adapter an empty container and
 * portal-renders its content into it. The adapter mounts the container
 * inside the host's modal widget (Magento `Magento_Ui/js/modal/modal`,
 * Shopware bootstrap modal, …) and wires the close-button / ESC /
 * backdrop to `onClose`. The bundle's close-handle is invoked when the
 * React side wants to dismiss (e.g. single-pick auto-close after a
 * selection).
 */
function AdapterOverlay({
    adapter, title, onClose, children,
}: { adapter: ModalAdapter; title: string; onClose: () => void; children: ReactNode }) {
    // Mirror the host's embedMode onto the portaled container so the
    // storefront typography/chrome resets apply inside the host modal
    // too — otherwise listimage tile labels keep rendering in the
    // bundle's mini-Schrift even though the surrounding configurator is
    // already host-theme neutral.
    const preview = useWebshopContext();
    // The container handed to the adapter carries the `cfg-portaled`
    // class so the design tokens (--cfg-space-*, --cfg-color-*, …) cascade
    // back in. Host-shop modal widgets (Magento_Ui, Bootstrap modal, …)
    // move the container out of the `.cfg-root` DOM subtree; without the
    // duplicate token declaration the tile grid would collapse to a
    // `gap: 0` row because `var(--cfg-space-2)` would resolve to empty.
    const [container] = useState(() => {
        if (typeof document === 'undefined') return null;
        const el = document.createElement('div');
        el.className = 'cfg-portaled';
        if (preview?.embedMode) {
            el.setAttribute('data-embed-mode', preview.embedMode);
        }
        return el;
    });
    useThemeStyleOn(container);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!container) return;
        const handle = adapter.openOverlay({
            title,
            container,
            onClose: () => onCloseRef.current(),
        });
        return () => handle.close();
        // Adapter, title, and container are stable for the lifetime of
        // the overlay — re-open on re-render would tear down the host
        // modal and respin a new one on every state change inside the
        // grid. Deps are intentionally empty.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!container) return null;
    return createPortal(children, container);
}

